import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import {
  superListSoundStyles,
  superCreateSoundStyle,
  superUpdateSoundStyle,
  superDeleteSoundStyle,
  type SuperSoundStyleRow,
} from "../../../lib/api";
import { useDialogs } from "../../../hooks/useDialogs";

// CRUD de estilos de sonido. Lista + form inline para crear. Cada row:
// editable label/description/activo + borrar. El admin de sucursal usa
// esta lista (solo activos) en su selector.
export function SoundStylesCRUD() {
  const dialogs = useDialogs();
  const [rows, setRows] = useState<SuperSoundStyleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);

  function refetch() {
    setError(null);
    superListSoundStyles()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(() => {
    refetch();
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await superCreateSoundStyle({
        slug: newSlug.trim(),
        label: newLabel.trim(),
        description: newDesc.trim() || null,
      });
      setShowForm(false);
      setNewSlug("");
      setNewLabel("");
      setNewDesc("");
      refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleActivo(row: SuperSoundStyleRow) {
    try {
      await superUpdateSoundStyle(row.id, { activo: row.activo !== 1 });
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  async function handleDelete(row: SuperSoundStyleRow) {
    const ok = await dialogs.confirm({
      title: "Borrar estilo de sonido",
      message: `¿Borrar "${row.label}"? Solo se puede si NINGUNA sucursal lo tiene elegido. Para "desactivar" sin perder los archivos, mejor toggle "Activo".`,
      confirmLabel: "Borrar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await superDeleteSoundStyle(row.id);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded bg-red-500/15 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
      <div className="space-y-2">
        {rows === null ? (
          <div className="text-xs opacity-60">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs opacity-60">
            No hay estilos cargados. Crea el primero abajo.
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 p-3"
            >
              <div className="min-w-0 flex-1">
                {/* El slug técnico se oculta de la vista normal — vive en
                    el title del nombre como tooltip. */}
                <div
                  className="truncate text-sm font-medium"
                  title={r.slug}
                >
                  {r.label}
                </div>
                {r.description && (
                  <div className="mt-0.5 text-xs opacity-60">
                    {r.description}
                  </div>
                )}
              </div>
              <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={r.activo === 1}
                  onChange={() => toggleActivo(r)}
                  className="h-3.5 w-3.5"
                />
                Activo
              </label>
              <button
                type="button"
                onClick={() => handleDelete(r)}
                className="shrink-0 rounded p-1.5 text-red-300 hover:bg-red-500/15"
                title="Borrar"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {showForm ? (
        <form
          onSubmit={handleCreate}
          className="space-y-2 rounded-lg border border-[#a78bfa]/40 bg-[#a78bfa]/5 p-3"
        >
          <div className="text-xs font-medium opacity-80">Nuevo estilo</div>
          <input
            type="text"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
            placeholder="slug (ej: 'navidad')"
            pattern="[a-z0-9-]+"
            title="Solo minúsculas, números y guiones"
            required
            className="w-full rounded bg-white/10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
          />
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (ej: 'Navidad')"
            required
            className="w-full rounded bg-white/10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
          />
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Descripción (opcional, ej: 'Cascabeles + coro')"
            className="w-full rounded bg-white/10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
          />
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded px-3 py-1 text-xs opacity-70 hover:bg-white/5 hover:opacity-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-3 py-1 text-xs font-bold disabled:opacity-50"
            >
              Crear
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full rounded-lg border-2 border-dashed border-white/10 px-3 py-2 text-xs opacity-60 hover:border-white/30 hover:opacity-100"
        >
          + Nuevo estilo
        </button>
      )}

      <p className="text-xs opacity-50">
        Al crear un estilo nuevo, su música de fanfarria se sube abajo en
        "Sonidos antiguos". El admin de sucursal lo verá en su selector si está
        activo.
      </p>
    </div>
  );
}
